import { useCallback, useEffect, useMemo, useState } from 'react';

import { type AdminApproval, AdminApprovalStatus, AdminApprovalType } from '@column/api';

export const MAX_INVITE_SELECTION = 100;

export const isReviewableInvite = (approval: AdminApproval) =>
  approval.type === AdminApprovalType.USER_INVITE &&
  approval.can_review &&
  approval.status === AdminApprovalStatus.PENDING &&
  new Date(approval.expires_at) > new Date();

export const useInviteApprovalSelection = (approvals: AdminApproval[], contextKey: string) => {
  const [selection, setSelection] = useState({ contextKey, ids: new Set<string>() });
  const eligible = useMemo(() => approvals.filter(isReviewableInvite), [approvals]);
  const eligibleIds = useMemo(() => new Set(eligible.map((approval) => approval.id)), [eligible]);
  const selected = eligible.filter((approval) => selection.contextKey === contextKey && selection.ids.has(approval.id));
  const selectedIds = new Set(selected.map((approval) => approval.id));

  useEffect(() => {
    setSelection((previous) => {
      const ids = new Set([...previous.ids].filter((id) => eligibleIds.has(id)));
      if (previous.contextKey !== contextKey) return { contextKey, ids: new Set<string>() };
      return ids.size === previous.ids.size ? previous : { contextKey, ids };
    });
  }, [contextKey, eligibleIds]);

  const setSelected = useCallback(
    (ids: string[], checked: boolean) => {
      setSelection((previous) => {
        const next = new Set(
          previous.contextKey === contextKey ? [...previous.ids].filter((id) => eligibleIds.has(id)) : []
        );
        ids.forEach((id) => {
          if (!checked) next.delete(id);
          else if (eligibleIds.has(id) && next.size < MAX_INVITE_SELECTION) next.add(id);
        });
        return { contextKey, ids: next };
      });
    },
    [contextKey, eligibleIds]
  );
  const clear = useCallback(() => setSelection({ contextKey, ids: new Set<string>() }), [contextKey]);
  const allSelected: boolean | 'indeterminate' =
    selected.length === 0
      ? false
      : selected.length === Math.min(eligible.length, MAX_INVITE_SELECTION)
        ? true
        : 'indeterminate';

  return {
    eligible,
    eligibleIds,
    selected,
    selectedIds,
    allSelected,
    atLimit: selected.length >= MAX_INVITE_SELECTION,
    setSelected,
    clear,
  };
};
